import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useMedia } from "react-use";

import { Button } from "@/components/Button";
import { Header } from "@/components/Header";
import { Text } from "@/components/Text";
import { MEDIA_SIZE_SM } from "@/core/media";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { Flex } from "@/eworkitComponents";
import { AnimatedLayout } from "@/views/components/Layout/components/AnimatedLayout";
import { AnimatedLayoutContainer } from "@/views/components/Layout/components/AnimatedLayoutContainer";
import { useOnboardingAcceptProfiles } from "@/views/Onboarding/hooks";

import { OnboardingProfilesHeader } from "./OnboardingProfilesHeader";
import { OnboardingProfilesRecord } from "./OnboardingProfilesRecord";

import "./_onboarding-profiles-list.scss";

interface IOnboardingProfileTypeBoxProps {
  pendingProfiles: IConsultantProfile[];
  firstName: string;
}

function sortProfiles(a: IConsultantProfile, b: IConsultantProfile) {
  if (a.personalInvitation) {
    return -1;
  }
  if (a.createdDate < b.createdDate) {
    return 1;
  }
  return 0;
}

export const OnboardingProfilesList: FC<IOnboardingProfileTypeBoxProps> = () => {
  const { t } = useTranslation("session");
  const history = useHistory();
  const location = useLocation<any>();
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);
  const { handleContinueClick } = useOnboardingAcceptProfiles();

  if (!location.state) {
    history.goBack();
  }
  const { pendingProfiles, firstName }: IOnboardingProfileTypeBoxProps = location.state;
  const profiles = pendingProfiles.sort(sortProfiles);

  const recommendedJobRequestId = profiles[0].personalInvitation
    ? profiles[0].personalInvitation.recommendedJobRequestId
    : undefined;

  const { jobRequestApplication } = profiles[0].personalInvitation || {};

  const handleContinue = () => handleContinueClick(profiles[0].id, recommendedJobRequestId, jobRequestApplication);

  return (
    <AnimatedLayout>
      <AnimatedLayoutContainer size="lg" showLogo>
        <Flex direction="column" padding={["md"]} innerMargin="xs" className="onboarding-profile-list">
          <Header color="black" size="lg" align="center" weight="bold" className="-word-break-all" hjSuppress>
            {t("ONBOARDING.PROFILES_LIST.HEADER", { value: firstName })}
          </Header>
          <Text align="center" color="grey1">
            {t(`ONBOARDING.PROFILES_LIST.${profiles.length > 1 ? "WAITING_PROFILES" : "WAITING_PROFILE"}`)}
          </Text>

          <Flex direction={isSmallScreen ? "row" : "column"} padding={["sm", false, "lg", false]}>
            <OnboardingProfilesHeader />
            {profiles.map(p => (
              <OnboardingProfilesRecord key={p.id} profile={p} />
            ))}
          </Flex>
          <Flex justify="flex-end" padding={["sm", false, false, false]}>
            <Button size="lg" onClick={handleContinue} className="continue-button">
              <Text>{t("ONBOARDING.PROFILES_LIST.LIST.BUTTON_CONTINUE")}</Text>
            </Button>
          </Flex>
        </Flex>
      </AnimatedLayoutContainer>
    </AnimatedLayout>
  );
};
